import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function LoginPage() {
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);

  const navigate  = useNavigate();
  const location  = useLocation();
  const { login } = useAuth();
  const from = location.state?.from || null;

  const handlePostLogin = (data) => {
    login(data.access_token, {
      id: data.user_id, email: data.email,
      full_name: data.full_name, role: data.role,
    });
    if (from) { navigate(from); return; }
    if (!data.onboarding_completed) {
      navigate(data.role === 'landlord' ? '/onboarding/landlord' : '/onboarding/renter');
    } else {
      navigate(data.role === 'landlord' ? '/landlord-dashboard' : '/student-dashboard');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const form = new URLSearchParams();
      form.append('username', email);
      form.append('password', password);
      const res  = await fetch(`${API}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: form.toString(),
      });
      const data = await res.json();
      if (!res.ok) { setError(typeof data.detail === 'string' ? data.detail : 'Login failed.'); return; }
      handlePostLogin(data);
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError('');
      try {
        const res  = await fetch(`${API}/api/auth/google-direct/verify`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: tokenResponse.access_token, intended_role: 'student' }),
        });
        const data = await res.json();
        if (!res.ok) { setError(typeof data.detail === 'string' ? data.detail : 'Google sign-in failed'); return; }
        handlePostLogin(data);
      } catch { setError('Google sign-in failed. Please try again.'); }
    },
    onError: () => setError('Google sign-in was cancelled or failed.'),
    flow: 'implicit',
  });

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg,#f0f7fa 0%,#e8f4f8 100%)' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="h-1.5 w-full" style={{ background: BRAND }} />
          <div className="p-8">
            {/* Logo */}
            <Link to="/" className="flex items-center justify-center gap-0 mb-6">
              <span className="text-3xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
              <img src="/rentalx-x-new.svg" alt="X"
                style={{ height: '2rem', width: 'auto', marginLeft: '0.05em', marginBottom: '0.03em', verticalAlign: 'middle' }} />
            </Link>

            <div className="text-center mb-7">
              <h1 className="text-2xl font-black text-gray-900 mb-1">Welcome back</h1>
              <p className="text-sm text-gray-400">Sign in to your renter account</p>
            </div>

            {/* OAuth */}
            <div className="space-y-2.5 mb-5">
              <button onClick={() => googleLogin()}
                className="w-full h-11 border border-gray-200 rounded-xl flex items-center justify-center gap-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="google-login-btn">
                <GoogleIcon /> Continue with Google
              </button>
              <button className="w-full h-11 border border-gray-200 rounded-xl flex items-center justify-center gap-2.5 text-sm font-medium text-gray-400 cursor-not-allowed">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
                <span className="ml-auto text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded font-medium">Coming Soon</span>
              </button>
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs text-gray-400">or sign in with email</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600"
                data-testid="login-error">{error}</div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full h-12 border border-gray-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/25 focus:border-[#094f6f] transition-colors"
                  required data-testid="login-email" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password}
                    onChange={e => setPassword(e.target.value)} placeholder="Enter your password"
                    className="w-full h-12 border border-gray-200 rounded-xl px-4 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/25 focus:border-[#094f6f] transition-colors"
                    required data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full h-12 rounded-xl font-bold text-white text-sm disabled:opacity-50 hover:opacity-90 transition-opacity"
                style={{ background: BRAND }} data-testid="login-submit">
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-gray-100 text-center space-y-3">
              <p className="text-sm text-gray-500">
                Don&apos;t have an account?{' '}
                <Link to="/register/student" className="font-semibold text-[#094f6f] hover:underline">Sign up</Link>
              </p>
              <Link to="/landlord/login"
                className="block text-xs text-gray-400 hover:text-[#094f6f] transition-colors">
                Property Manager or Landlord? Sign in here →
              </Link>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-gray-400 mt-4">
          By signing in you agree to our{' '}
          <Link to="/terms" className="underline hover:text-gray-600">Terms</Link> and{' '}
          <Link to="/privacy" className="underline hover:text-gray-600">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
